import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Portal } from "@chakra-ui/portal";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes from "routes";
import { useAuth } from "contexts/authContext";
import { useQuery } from "@tanstack/react-query";
import { getUserProfile } from "api";
// import PhoneVerificationBanner from "components/banners/PhoneVerificationBanner"; // COMMENTED OUT - Phone banner not functioning

const LoadingSpinner = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-16 w-16 animate-spin rounded-full border-4 border-dashed border-orange-500" />
  </div>
);

export default function Admin({ mini, theme, setTheme, setMini, ...rest }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, isAuthenticated, loading: authLoading } = useAuth();

  const [open, setOpen] = useState(window.innerWidth >= 768);
  const [hovered, setHovered] = useState(false);
  const [currentRoute, setCurrentRoute] = useState("Main Dashboard");
  const [loading, setLoading] = useState(true);
  // const [showBanner, setShowBanner] = useState(false); // COMMENTED OUT - Phone banner not functioning

  // const dismissed = localStorage.getItem("hidePhoneBanner") === "true"; // COMMENTED OUT - Phone banner not functioning

  const { data: profile, isLoading: profileLoading, refetch } = useQuery({
    queryKey: ["userProfile", isAuthenticated, user?.id],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const response = await getUserProfile();
      console.log("useQuery: Fetched user profile:", JSON.stringify(response, null, 2));
      return response;
    },
    enabled: isAuthenticated,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.user) {
        setUser({ ...data.user, permissions: data.user.permissions || ["USER_VIEW"] });
        // COMMENTED OUT - Phone banner not functioning
        // if (data.user.phoneVerified) {
        //   setShowBanner(false);
        //   localStorage.removeItem("hidePhoneBanner");
        // }
      }
    },
    onError: (err) => {
      console.error("useQuery: Failed to fetch user profile:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
    },
  });

  // Handle location state changes
  useEffect(() => {
    if (location.state?.from === "phone-verification") {
      refetch();
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, refetch]);

  // COMMENTED OUT - Phone banner not functioning
  // useEffect(() => {
  //   if (authLoading || profileLoading) return;
  //   if (
  //     isAuthenticated &&
  //     profile?.user &&
  //     profile.user.phoneVerified === false &&
  //     !dismissed
  //   ) {
  //     console.log("Showing phone verification banner: phoneVerified is false");
  //     setShowBanner(true);
  //   } else {
  //     setShowBanner(false);
  //     localStorage.removeItem("hidePhoneBanner");
  //   }
  // }, [profile, dismissed, isAuthenticated, authLoading, profileLoading]);

  // COMMENTED OUT - Phone banner not functioning
  // const dismissBanner = () => {
  //   setShowBanner(false);
  //   localStorage.setItem("hidePhoneBanner", "true");
  // };

  // COMMENTED OUT - Phone banner not functioning
  // const handleVerifyPhone = () => {
  //   navigate("/auth/phone-verification", {
  //     state: { type: "phone" },
  //   });
  // };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    const currentRoute = routes.find((route) => route.path === currentPath);
    if (currentRoute) {
      setCurrentRoute(currentRoute.name);
    }
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const hasSecondaryNavbar = (routes) => {
    const currentPath = location.pathname;
    const currentRoute = routes.find((route) => route.path === currentPath);
    return currentRoute?.secondary;
  };

  const renderRoutes = (routes) => {
    return routes.map((route, index) => {
      if (route.layout === "/admin") {
        return (
          <Route
            key={index}
            path={route.path}
            element={route.component}
          />
        );
      } else {
        return null;
      }
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (authLoading || profileLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  if (profile?.error) {
    return <div>Error loading profile. Please try refreshing the page.</div>;
  }

  return (
    <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
      <Sidebar
        open={open}
        hovered={hovered}
        setHovered={setHovered}
        mini={mini}
        onClose={() => setOpen(false)}
      />
      <div className="h-full w-full font-dm dark:bg-background-900">
        <main
          className={`mx-2.5 flex-none transition-all dark:bg-background-900 md:pr-2 ${
            mini === false
              ? "lg:ml-0 xl:ml-[313px]"
              : mini && hovered
              ? "lg:ml-0 xl:ml-[313px]"
              : "lg:ml-0 xl:ml-[142px]"
          }`}
        >
          <Portal>
            <Navbar
              onOpenSidenav={() => setOpen(!open)}
              brandText={currentRoute}
              secondary={hasSecondaryNavbar(routes)}
              theme={theme}
              setTheme={setTheme}
              hovered={hovered}
              mini={mini}
              setMini={setMini}
              {...rest}
            />
          </Portal>
          <div className="mx-auto min-h-screen px-2 md:px-4 pt-[100px]">
            {/* COMMENTED OUT - Phone banner not functioning */}
            {/* {showBanner && (
              <div className="mb-4">
                <PhoneVerificationBanner
                  onVerify={handleVerifyPhone}
                  onDismiss={dismissBanner}
                />
              </div>
            )} */}
            <Routes>
              {renderRoutes(routes)}
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </div>
          <div className="p-3">
            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
}
